// @ts-check

/**
 * FVBStoreSearch is a custom web component that renders a store search bar.
 *
 * Store data is embedded server-side as JSON in a `[data-store-search-json]`
 * script tag inside the component. Shopify's `metaobjects.<type>.values`
 * caps out at 50 entries by default; store-search-bar.liquid works around
 * this by wrapping it in `{% paginate ... by <total count> %}`, which pulls
 * every store onto a single page instead of the default 50.
 */

/**
 * @typedef {Object} Store
 * @property {string} key - The unique identifier for the store.
 * @property {string} name - The store name.
 * @property {string} address - The store address.
 * @property {string} type - The store type.
 * @property {number} lat - The latitude of the store.
 * @property {number} lng - The longitude of the store.
 * @property {string} phone - The phone number of the store.
 * @property {string} website - The website URL of the store.
 * @property {string} url - The store URL.
 * @property {number | undefined} distance - The distance to the store.
 */

if (!customElements.get('fvb-store-search')) {
  class FVBStoreSearch extends HTMLElement {
    /** @type {Array<Store>} */
    stores = [];
    constructor() {
      super();
    }

    connectedCallback() {
      const rawStores = this.readStoresJson(this);

      if (!rawStores.length) {
        console.warn('No stores found for FVBStoreSearch component. Please ensure the store metaobjects are published and "[data-store-search-json]" contains valid data.');
        return;
      }

      this.stores = rawStores.map((/** @type {any} */ store) => {
        // Ensure each store has a name and address for demonstration purposes
        return {
          name: store.name,
          address: store.address,
          type: store.type,
          lat: !isNaN(store.latitude) ? parseFloat(store.latitude) : undefined,
          lng: !isNaN(store.longitude) ? parseFloat(store.longitude) : undefined,
          phone: store.phone,
          website: store.website,
          url: store.url
        };
      }).filter((/** @type {any} */ store) => store.address && store.lat && store.lng);

      if (!this.stores.length) {
        return;
      }

      this.initSearchAutocomplete();
      this.initUseLocationButton();
    }

    /**
     * Reads and parses the JSON store data embedded by
     * store-search-bar.liquid in a `[data-store-search-json]` script tag.
     * @param {ParentNode} root
     * @returns {Array<any>}
     */
    readStoresJson(root) {
      const script = root.querySelector('[data-store-search-json]');
      if (!script?.textContent) {
        return [];
      }

      try {
        const parsed = JSON.parse(script.textContent);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Failed to parse store search JSON data:', error);
        return [];
      }
    }

    initUseLocationButton() {
      const locationButton = this.querySelector('.js-use-location');
      if (!locationButton) {
        return;
      }

      locationButton.addEventListener('click', (event) => {
        event.preventDefault();
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(position => {
            const { latitude, longitude } = position.coords;
            console.log ('User location:', latitude, longitude);
            this.showStoresByLocation(latitude, longitude);
          }, error => {
            console.error('Error getting location:', error);
            alert('Unable to retrieve your location. Please allow location access and try again.');
          });
        } else {
          alert('Geolocation is not supported by your browser.');
        }
      });
    }
  
    initSearchAutocomplete() {
      /** @type {HTMLInputElement | null} */
      const input = this.querySelector('.store-search-bar__input');

      /** @type {HTMLFormElement| null} */
      const form = this.querySelector('form');

      if (!input || !form) {
        return;
      }

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const query = input.value.trim().toLowerCase();
        this.showStores(query);
      });

      this.initGoogleAddressAutocomplete(input);
    }

    /**
     * Layers Google Places address autocomplete onto the existing search
     * input, as-you-type, using the new (headless) Places API — the legacy
     * `google.maps.places.Autocomplete` widget is closed to new customers
     * as of March 2025. Picking a suggestion resolves its coordinates and
     * runs the same nearest-stores search as "Use my location". Entirely
     * optional: with no API key configured, or if Google's API fails to
     * load/respond (e.g. the required "Places API (New)" isn't enabled for
     * the key), the input keeps working as a plain text search.
     * @param {HTMLInputElement} input
     */
    initGoogleAddressAutocomplete(input) {
      const apiKey = this.dataset.googleMapsApiKey;
      if (!apiKey) {
        return;
      }

      const field = input.closest('.store-search-bar__field');
      if (!field) {
        return;
      }

      const countryRestriction = (this.dataset.googleMapsCountryRestriction || '')
        .split(',')
        .map(code => code.trim().toLowerCase())
        .filter(Boolean);

      const list = document.createElement('ul');
      list.className = 'store-search-bar__autocomplete';
      list.id = `${input.id || 'StoreSearchBar'}-autocomplete`;
      list.setAttribute('role', 'listbox');
      list.hidden = true;
      field.appendChild(list);

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', list.id);
      input.setAttribute('autocomplete', 'off');

      /** @type {Array<any>} suggestions currently rendered in the dropdown */
      let activeSuggestions = [];
      let activeIndex = -1;
      let sessionToken = null;
      /** @type {any} the places library namespace, once loaded */
      let placesLibrary = null;

      const hideList = () => {
        list.hidden = true;
        list.innerHTML = '';
        activeSuggestions = [];
        activeIndex = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      };

      const renderList = () => {
        list.innerHTML = '';
        activeSuggestions.forEach((suggestion, index) => {
          const item = document.createElement('li');
          item.id = `${list.id}-option-${index}`;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', String(index === activeIndex));
          item.textContent = suggestion.placePrediction.text.toString();
          item.addEventListener('mousedown', (event) => {
            // mousedown (not click) so it fires before the input's blur.
            event.preventDefault();
            this.selectGoogleSuggestion(suggestion);
            hideList();
          });
          list.appendChild(item);
        });
        list.hidden = activeSuggestions.length === 0;
        input.setAttribute('aria-expanded', String(!list.hidden));
      };

      const search = this.debounce(async (/** @type {string} */ query) => {
        if (!placesLibrary || query.length < 3) {
          hideList();
          return;
        }

        try {
          if (!sessionToken) {
            sessionToken = new placesLibrary.AutocompleteSessionToken();
          }

          const request = { input: query, sessionToken };
          if (countryRestriction.length) {
            request.includedRegionCodes = countryRestriction;
          }

          const { suggestions } = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          activeSuggestions = suggestions || [];
          activeIndex = -1;
          renderList();
        } catch (error) {
          console.error('Google Places autocomplete request failed:', error);
          hideList();
        }
      }, 250);

      input.addEventListener('input', () => {
        search(input.value.trim());
      });

      input.addEventListener('keydown', (event) => {
        if (list.hidden) {
          return;
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          activeIndex = Math.min(activeIndex + 1, activeSuggestions.length - 1);
          renderList();
          input.setAttribute('aria-activedescendant', `${list.id}-option-${activeIndex}`);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          activeIndex = Math.max(activeIndex - 1, 0);
          renderList();
          input.setAttribute('aria-activedescendant', `${list.id}-option-${activeIndex}`);
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          event.preventDefault();
          const suggestion = activeSuggestions[activeIndex];
          this.selectGoogleSuggestion(suggestion);
          hideList();
        } else if (event.key === 'Escape') {
          hideList();
        }
      });

      input.addEventListener('blur', () => {
        // Delayed so a suggestion's mousedown handler still fires first.
        setTimeout(hideList, 100);
      });

      this.loadGoogleMapsPlacesLibrary(apiKey)
        .then((library) => {
          placesLibrary = library;
          sessionToken = new library.AutocompleteSessionToken();
        })
        .catch((error) => {
          console.error('Google Maps address autocomplete is unavailable:', error);
        });
    }

    /**
     * Resolves a picked Google autocomplete suggestion to a lat/lng and
     * runs the same nearest-stores search as "Use my location".
     * @param {any} suggestion
     */
    async selectGoogleSuggestion(suggestion) {
      try {
        /** @type HTMLInputElement | null */
        const input = this.querySelector('.store-search-bar__input');
        const placeText = suggestion.placePrediction.text.toString();
        const place = suggestion.placePrediction.toPlace();
        await place.fetchFields({ fields: ['location'] });

        const location = place.location;
        if (!location) {
          return;
        }

        // google.maps.LatLng normally exposes lat()/lng() as methods, but
        // guard for a plain {lat, lng} literal too since that shape shows
        // up in some Places API (New) responses/docs.
        const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
        const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

        if (typeof lat === 'number' && typeof lng === 'number') {
          if (input && placeText) {
            input.value = placeText;
          }
          this.showStoresByLocation(lat, lng);
        }
      } catch (error) {
        console.error('Failed to resolve the selected place:', error);
      }
    }

    /**
     * Loads the Google Maps JavaScript API (once per page, shared across
     * any instances of this component) and returns its "places" library.
     * @param {string} apiKey
     * @returns {Promise<any>}
     */
    loadGoogleMapsPlacesLibrary(apiKey) {
      // @ts-ignore
      if (!window.__fvbGoogleMapsPromise) {
        // @ts-ignore
        window.__fvbGoogleMapsPromise = new Promise((resolve, reject) => {
          // @ts-ignore
          if (window.google?.maps?.importLibrary) {
            // @ts-ignore
            resolve(window.google.maps);
            return;
          }

          // Google calls this global callback instead of throwing when the
          // key is invalid, restricted to the wrong referrer, or billing
          // isn't enabled — catching it gives a much clearer error than
          // waiting for the generic "did not initialize" fallback below.
          // @ts-ignore
          const previousAuthFailure = window.gm_authFailure;
          // @ts-ignore
          window.gm_authFailure = () => {
            reject(new Error(
              'Google Maps authentication failed. Check that the API key is valid, unrestricted for this domain, ' +
              'has billing enabled, and has "Places API (New)" enabled in the Google Cloud Console.'
            ));
            // @ts-ignore
            previousAuthFailure?.();
          };

          // A successful `onload` can fire a tick before Maps finishes
          // attaching `importLibrary` to the global, so poll briefly
          // instead of failing on the very first check.
          const waitForImportLibrary = (/** @type {number} */ deadline) => {
            // @ts-ignore
            if (window.google?.maps?.importLibrary) {
              // @ts-ignore
              resolve(window.google.maps);
            } else if (Date.now() < deadline) {
              setTimeout(() => waitForImportLibrary(deadline), 50);
            } else {
              reject(new Error('Google Maps script loaded but did not initialize.'));
            }
          };

          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly&loading=async`;
          script.async = true;
          script.onload = () => waitForImportLibrary(Date.now() + 2000);
          script.onerror = () => reject(new Error('Failed to load the Google Maps script.'));
          document.head.appendChild(script);
        });
      }

      // @ts-ignore
      return window.__fvbGoogleMapsPromise.then((/** @type {any} */ maps) => maps.importLibrary('places'));
    }

    /**
     * @param {Function} fn
     * @param {number} wait
     */
    debounce(fn, wait) {
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let timeout;
      return (/** @type {any[]} */ ...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn(...args), wait);
      };
    }

    // @ts-ignore
    showStoresByLocation(latitude, longitude) {
      const storesWithDistance = this.stores.map(store => {
        const distance = this.calculateDistance(latitude, longitude, store.lat, store.lng);
        return { ...store, distance };
      }).filter(store => store.distance !== undefined)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 12); // Show top 12 closest stores
      
      /** @type {HTMLElement | null} */
      const storesContainer = this.querySelector('.store-selection-block');

      if (!storesContainer) {
        return;
      }

      storesContainer.innerHTML = ''; // Clear previous results
    
      if (storesWithDistance.length === 0) {
        storesContainer.innerHTML = `<p>${this.dataset.noResultsText || 'No stores found.'}</p>`;
        return;
      }
    
      const resultsContainer = document.createElement('ul');
      resultsContainer.classList.add('store-selection-list', 'local-stores');

      storesWithDistance.forEach(store => {
        const storeElement = document.createElement('li');
        storeElement.innerHTML = `
          <div class="store-selection-info">
            <div class="store__type">
              ${store.type === 'Brandstore' ? `
                <div class="fvb-badge" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16.044 14.09" role="presentation" focusable="false">
                    <polygon points="16.044 0 3.527 0 3.527 5.829 0 5.829 0 8.503 3.527 8.503 3.527 14.09 7.722 14.082 7.722 8.614 15.006 8.614 15.006 6.053 7.722 6.053 7.722 2.737 16.044 2.737 16.044 0" fill="#010000"/>
                  </svg>
                </div>
              ` : ''}
              ${store.type || ''}
            </div>
            <div class="store__name"><strong>${store.name}</strong></div>
            <div class="store__address">
              <address>
                ${store.address.replace(/\n/g, '<br>')}
              </address>
              ${store.phone ? `<a href="tel:${store.phone}" class="link--unstyled">${store.phone}</a>` : ''}
            </div>
            <div class="store__more">
              <div class="store__distance">${store.distance.toFixed(2)} km</div>
              ${store.website ? `<a href="${store.website}" target="_blank" class="link--unstyled">Website</a>` : `<a href="${store.url}" class="styled-read-more-link fit-content"><span class="label">${this.dataset.viewStoreLabel || 'View store'}</span><i class="icon icon-right-arrow"></i></a>`}
            </div>
          </div>
        `;
        resultsContainer.appendChild(storeElement);
      });
  
      storesContainer.appendChild(resultsContainer);

    }

    // @ts-ignore
    calculateDistance(lat1, lng1, lat2, lng2) {
      const toRadians = (/** @type {number} */ degrees) => degrees * Math.PI / 180;
      const R = 6371; // Earth radius in kilometers
      const dLat = toRadians(lat2 - lat1);
      const dLng = toRadians(lng2 - lng1);
      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
                Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c; // Distance in kilometers
    }

    showStores(/** @type {string} */ query) {
      /** @type {HTMLElement | null} */
      const storesContainer = this.querySelector('.store-selection-block');

      if (!storesContainer) {
        return;
      }

      storesContainer.innerHTML = ''; // Clear previous results
      const matchingStores = this.stores.filter(store => store.name.toLowerCase().includes(query) || store.address.toLowerCase().includes(query));
    
      if (matchingStores.length === 0) {
        storesContainer.innerHTML = `<p>${this.dataset.noResultsText || 'No stores found.'}</p>`;
        return;
      }
    
      const resultsContainer = document.createElement('ul');
      resultsContainer.classList.add('store-selection-list', 'local-stores');

      matchingStores.forEach(store => {
        const storeElement = document.createElement('li');
        storeElement.innerHTML = `
          <div class="store-selection-info">
            <div class="store__type">
              ${store.type === 'Brandstore' ? `
                <div class="fvb-badge" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16.044 14.09" role="presentation" focusable="false">
                    <polygon points="16.044 0 3.527 0 3.527 5.829 0 5.829 0 8.503 3.527 8.503 3.527 14.09 7.722 14.082 7.722 8.614 15.006 8.614 15.006 6.053 7.722 6.053 7.722 2.737 16.044 2.737 16.044 0" fill="#010000"/>
                  </svg>
                </div>
              ` : ''}
              ${store.type || ''}
            </div>
            <div class="store__name"><strong>${store.name}</strong></div>
            <div class="store__address">
              <address>
                ${store.address.replace(/\n/g, '<br>')}
              </address>
              ${store.phone ? `<a href="tel:${store.phone}" class="link--unstyled">${store.phone}</a>` : ''}
            </div>
            <div class="store__more">
              ${store.website ? `<a href="${store.website}" target="_blank" class="link--unstyled">Website</a>` : `<a href="${store.url}" class="styled-read-more-link fit-content"><span class="label">${this.dataset.viewStoreLabel || 'View store'}</span><i class="icon icon-right-arrow"></i></a>`}
            </div>
          </div>
        `;
        resultsContainer.appendChild(storeElement);
      });
  
      storesContainer.appendChild(resultsContainer);
    }

  }

  customElements.define('fvb-store-search', FVBStoreSearch);
}
